import React, {createContext, useContext, useEffect, useMemo, useState} from 'react'
import {useLocation} from 'react-router-dom'
import useOcapiSite from '../../hooks/useOcapiSite'
import {removeSiteLocaleFromPath} from '../../utils/url'
import PropTypes from 'prop-types'
import useEffectEvent from '../../hooks/use-effect-event'
import logger from '../../utils/logger-instance'

const ZendeskChatContext = createContext()

export const ZendeskChatProvider = ({children}) => {
    const location = useLocation()
    const {zendeskChatEnabled, zendeskChatKey, zendeskChatExcludedPages} =
        useOcapiSite().preferences || {}

    const [isLoaded, setIsLoaded] = useState(false)
    const [isVisible, setIsVisible] = useState(true)
    const [isOpened, setIsOpened] = useState(false)
    const [shouldLoad, setShouldLoad] = useState(false)

    // Parse excluded pages
    const excludedPages = useMemo(
        () => zendeskChatExcludedPages?.split(',').map((page) => page.trim()) || [],
        [zendeskChatExcludedPages]
    )

    // Determine if Zendesk chat should be active on this page
    const isEnabled = useMemo(() => {
        if (!zendeskChatEnabled || !zendeskChatKey) return false
        if (!excludedPages.length) return true
        const pathWithoutLocale = removeSiteLocaleFromPath(location.pathname)
        return !excludedPages.some((path) => pathWithoutLocale.startsWith(path))
    }, [zendeskChatEnabled, zendeskChatKey, excludedPages, location.pathname])

    const requestLoad = useEffectEvent(() => {
        if (!isEnabled) return
        setShouldLoad(true)
    })

    const STYLE_ID = 'zendesk-offset-style'

    const ensureStyle = () => {
        if (document.getElementById(STYLE_ID)) return
        const style = document.createElement('style')
        style.id = STYLE_ID
        style.textContent = `
            :root { --zendesk-offset: 0px; }

            iframe#launcher {
            bottom: calc(16px + var(--zendesk-offset)) !important;
            }

            /* div с X + Chat */
            [data-zd-actions="1"] {
            bottom: calc(88px + var(--zendesk-offset)) !important;
            }
        `
        document.head.appendChild(style)
    }

    const applyZendeskOffset = (py) => {
        ensureStyle()
        document.documentElement.style.setProperty('--zendesk-offset', py)

        const launcher = document.getElementById('launcher')
        const parent = launcher?.parentElement
        if (!parent) return

        const actions = [...parent.children].find(
            (el) => el.tagName === 'DIV' && el !== launcher && el.style.position === 'fixed'
        )
        if (actions) {
            actions.setAttribute('data-zd-actions', '1')
        }
    }

    const watchZendesk = (py) => {
        applyZendeskOffset(py)

        const obs = new MutationObserver(() => {
            const launcher = document.getElementById('launcher')
            if (launcher) {
                applyZendeskOffset(py)
                obs.disconnect()
            }
        })

        obs.observe(document.body, {childList: true, subtree: true})
        return () => obs.disconnect()
    }

    const setBottomOffset = useEffectEvent((value) => {
        const py = typeof value === 'number' ? `${value}px` : value
        watchZendesk(py)
    })

    // Load Zendesk script
    useEffect(() => {
        if (!isEnabled || !shouldLoad || isLoaded) return

        const script = document.createElement('script')
        script.id = 'ze-snippet'
        script.src = `https://static.zdassets.com/ekr/snippet.js?key=${zendeskChatKey}`
        script.async = true
        script.defer = true
        script.onload = () => {
            const loadTime = Date.now()

            const initializeZendesk = async () => {
                logger.debug(
                    `Zendesk chat loaded in ${Date.now() - loadTime}ms, name: ${
                        window.zE.name
                    }, version: ${window.zE.version}`
                )
                setIsLoaded(true)
                try {
                    await window.zE('messenger:set', 'conversationFields', [
                        {id: '46010718065809', value: window.location.href}
                    ])
                } catch (e) {
                    logger.error('Failed to set conversationField 46010718065809', e)
                }
            }

            // Try immediately first
            if (window.zE) {
                initializeZendesk()
                return
            }

            // If not available, poll every 200ms
            const timer = setInterval(async () => {
                if (!window.zE) {
                    if (Date.now() - loadTime > 60000) {
                        logger.error('Zendesk chat failed to load after 60 seconds')
                        clearInterval(timer)
                    }
                    return
                }
                clearInterval(timer)
                await initializeZendesk()
            }, 200)
        }
        script.onerror = () => logger.error('Failed to load Zendesk chat')

        document.body.appendChild(script)
    }, [isEnabled, zendeskChatKey, shouldLoad, isLoaded])

    // Subscribe to Zendesk events
    useEffect(() => {
        if (!isLoaded || !window.zE) return

        const onOpen = () => setIsOpened(true)
        const onClose = () => setIsOpened(false)

        window.zE('messenger:on', 'open', onOpen)
        window.zE('messenger:on', 'close', onClose)

        //no cleanup for messenger
    }, [isLoaded])

    // Control visibility when disabled or hidden
    useEffect(() => {
        if (!isLoaded || !window.zE) return

        if (!isEnabled || !isVisible) {
            window.zE('messenger', 'hide')
        } else {
            window.zE('messenger', 'show')
        }
    }, [isLoaded, isEnabled, isVisible])

    // API functions
    const show = useEffectEvent(() => setIsVisible(true))
    const hide = useEffectEvent(() => setIsVisible(false))
    const open = useEffectEvent(() => {
        if (!isLoaded || !window.zE) return
        setIsVisible(true)
    })

    const ctx = useMemo(
        () => ({
            enabled: isEnabled,
            loaded: isLoaded,
            visible: isVisible,
            opened: isOpened,
            show,
            hide,
            open,
            requestLoad,
            setBottomOffset
        }),
        [isEnabled, isLoaded, isVisible, isOpened, open, hide, show, requestLoad, setBottomOffset]
    )

    return <ZendeskChatContext.Provider value={ctx}>{children}</ZendeskChatContext.Provider>
}

ZendeskChatProvider.propTypes = {
    children: PropTypes.node
}

export const useZendeskChat = () => {
    const ctx = useContext(ZendeskChatContext)
    if (!ctx) throw new Error('useZendeskChat must be used within a ZendeskChatProvider')
    return ctx
}
